import React from 'react';
import { Upload, CheckCircle2, Loader2, Download, Trash2 } from 'lucide-react';
import StlArchIcon from './StlArchIcon';

/** Libellé affiché : nom d'origine ; le nom stocké (caseId-upper.stl) reste hors UI principale. */
function resolveStlDisplayLabel(pendingFile, uploadedRecord) {
  if (pendingFile?.name) {
    return { label: pendingFile.name, hint: null };
  }
  const original = uploadedRecord?.originalName?.trim();
  if (original) {
    return { label: original, hint: null };
  }
  if (uploadedRecord?.name) {
    return { label: null, hint: 'Nom d\'origine non enregistré pour ce fichier' };
  }
  return { label: null, hint: null };
}

/**
 * Carte téléversement STL (sélection, envoi, validé avec nom conservé).
 */
export default function StlArchUploadCard({
  variant,
  inputId,
  pendingFile,
  uploadedRecord,
  isUploading = false,
  disabled = false,
  onFileChange,
  onRemove,
  onDownload,
}) {
  const isUpper = variant === 'upper';
  const title = isUpper ? 'Scan supérieur (upper)' : 'Scan inférieur (lower)';
  const archLabel = isUpper ? 'Arcade supérieure (maxillaire)' : 'Arcade inférieure (mandibulaire)';

  const isUploaded = Boolean(uploadedRecord) && !pendingFile;
  const isPending = Boolean(pendingFile) && !isUploaded;
  const { label: displayLabel, hint: displayHint } = resolveStlDisplayLabel(pendingFile, uploadedRecord);
  const showValidated = isUploaded;

  const borderClass = showValidated
    ? 'border-success/60 bg-success/5'
    : isPending
      ? 'border-primary/50 bg-primary/5'
      : 'border-border bg-muted/20';

  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground mb-2">
        {title}
      </label>
      <div
        className={`relative border-2 border-dashed rounded-xl p-5 sm:p-6 text-center transition-colors ${borderClass}`}
      >
        <input
          type="file"
          accept=".stl"
          onChange={onFileChange}
          className="hidden"
          id={inputId}
          disabled={disabled || isUploading}
        />
        <label
          htmlFor={inputId}
          className={`flex flex-col items-center gap-3 ${disabled || isUploading ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
        >
          <span className="sr-only">
            {isUpper
              ? 'Arcade supérieure — sélectionner un fichier STL'
              : 'Arcade inférieure — sélectionner un fichier STL'}
          </span>
          <StlArchIcon variant={variant} className="mx-auto shrink-0 opacity-90" />
          <p className="text-xs font-medium text-muted-foreground">{archLabel}</p>

          {isUploading ? (
            <div className="flex items-center gap-2 text-primary">
              <Loader2 className="w-5 h-5 animate-spin shrink-0" />
              <span className="text-sm font-medium">Téléversement en cours…</span>
            </div>
          ) : showValidated ? (
            <CheckCircle2 className="w-10 h-10 text-success shrink-0" strokeWidth={2} aria-hidden />
          ) : (
            <Upload className="w-9 h-9 text-muted-foreground shrink-0" aria-hidden />
          )}

          {displayLabel || displayHint ? (
            <div
              className={`w-full max-w-md mx-auto rounded-lg border px-3 py-2.5 text-left ${
                showValidated
                  ? 'border-success/40 bg-background shadow-sm'
                  : 'border-primary/30 bg-background'
              }`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
                {showValidated ? 'Nom du fichier' : isPending ? 'Fichier sélectionné' : 'Fichier'}
              </p>
              {displayLabel ? (
                <p
                  className="text-base sm:text-[15px] font-semibold text-foreground break-all leading-snug"
                  title={displayLabel}
                >
                  {displayLabel}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic leading-snug">{displayHint}</p>
              )}
              {pendingFile?.size ? (
                <p className="text-xs text-muted-foreground mt-1">
                  {(pendingFile.size / 1024 / 1024).toFixed(2)} Mo
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm font-medium text-muted-foreground">
              Cliquez pour sélectionner un STL
            </p>
          )}

          {showValidated ? (
            <p className="text-xs text-muted-foreground">Cliquez pour remplacer le fichier</p>
          ) : null}
        </label>
        {showValidated && (onDownload || onRemove) ? (
          <div
            className={`mt-3 grid gap-2 ${onDownload && onRemove ? 'grid-cols-2' : 'grid-cols-1'}`}
          >
            {onDownload ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDownload();
                }}
                disabled={isUploading}
                className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-border bg-background text-sm font-medium text-foreground hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <Download className="w-4 h-4 shrink-0" aria-hidden />
                Télécharger
              </button>
            ) : null}
            {onRemove ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove();
                }}
                disabled={disabled || isUploading}
                className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-destructive/35 bg-destructive/5 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <Trash2 className="w-4 h-4 shrink-0" aria-hidden />
                Supprimer
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
